import queryString from 'query-string';
import moment from 'moment';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isArray from 'lodash/isArray';
import _omitBy from 'lodash/omitBy';

import { DATE_UNITS, PRODUCT_TYPES, TRANSACTION_TYPES } from './constants';

/**
 * Updates URL path with query params
 * @param {Object} queryParamsObj query params to be converted into string
 */
const objectToQueryParamsStr = (queryParamsObj) => {
  const filteredQueryParamsObj = _omitBy(queryParamsObj, (value) => value === '');
  const queryParams = queryString.stringify({ ...filteredQueryParamsObj }, { arrayFormat: 'bracket' });

  return queryParams && `?${queryParams}`;
};

/**
 * Get query params as object
 * @param {String} queryParamStr query params as string
 */
const queryParamStringToObject = (queryParamStr) => queryString.parse(queryParamStr);

/**
 * Converts date range query params string into object
 * @param {String} dateRange array of date strings of size 2
 */
const queryParamsDateRangeToObject = (dateRange) => {
  if (!Array.isArray(dateRange) || dateRange.length !== 2) return null;

  const dateRangeUnordered = dateRange.map((dateStr) => {
    const date = moment(dateStr);

    if (!date.isValid()) return null;
    return date;
  });

  if (dateRangeUnordered[0] && dateRangeUnordered[1]) {
    const isOrdered = dateRangeUnordered[1].diff(dateRangeUnordered[0]);
    const newDateRange = [];

    if (isOrdered <= 0) {
      newDateRange.push(dateRangeUnordered[1]);
      newDateRange.push(dateRangeUnordered[0]);
      return newDateRange;
    }

    return [...dateRangeUnordered];
  }

  return null;
};

/**
 * Converts date query params string into object
 * @param {String} dateStr date range as query params string
 */
const queryParamsDateToObject = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  if (!date.isValid() || date.valueOf() >= moment().valueOf()) return null;

  return date;
};

/**
 * Parse query params for overview filter
 * @param {String} queryParams query params string
 * @param {Array} brandsList Array of all brands
 * @param {Array} outletsList Array of all outlets
 */
const parseFilterQueryParams = ({ queryParams, brandsAndOutlets }) => {
  const returnObj = {
    brands: null,
    outlets: null,
    dateRange: null,
    unit: null,
    transactionType: null,
    productType: null,
  };

  if (queryParams.length > 0) {
    const queryParamsObject = queryParamStringToObject(queryParams);
    const {
      brands,
      outlets,
      'dateRange[]': dateRange,
      unit,
      transactionType,
      'productType[]': productType,
    } = queryParamsObject;

    // Check brand is valid
    const validBrand = Object.entries(brandsAndOutlets).find(([id]) => brands === id);
    if (validBrand) {
      _set(returnObj, 'brands', validBrand[0]);
    }

    // Check outlet is valid and part of brands provided
    let validOutletId = null;
    if (outlets === 'all') {
      validOutletId = outlets;
    } else {
      const validOutletObj = _get(validBrand, [1, 'outlets'], []).find(({ id: outletId }) => outletId === outlets);
      validOutletId = _get(validOutletObj, 'id', null);
    }
    if (validOutletId) {
      _set(returnObj, 'outlets', validOutletId);
    }
    const parsedDateRange = queryParamsDateRangeToObject(dateRange);
    _set(returnObj, 'dateRange', parsedDateRange);

    if (Object.values(DATE_UNITS).includes(unit)) {
      _set(returnObj, 'unit', unit);
    }
    if (_isArray(productType) && productType.every((v) => Object.values(PRODUCT_TYPES).includes(v))) {
      // for multi params productType[]=all&productType[]=card
      _set(returnObj, 'productType', productType);
    } else if (Object.values(PRODUCT_TYPES).includes(productType)) {
      // for single params productType[]=all | productType[]=card
      _set(returnObj, 'productType', [productType]);
    }
    if (Object.values(TRANSACTION_TYPES).includes(transactionType)) {
      _set(returnObj, 'transactionType', transactionType);
    }
  }

  return returnObj;
};

const MAX_REDIRECT_LENGTH = 2048;
// eslint-disable-next-line no-control-regex
const CONTROL_OR_WHITESPACE = /[\x00-\x1F\x7F\s]/;
const URI_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

const isSafeRedirectPath = (value) => {
  if (typeof value !== 'string') return false;
  if (value === '') return false;
  if (value.length > MAX_REDIRECT_LENGTH) return false;
  if (CONTROL_OR_WHITESPACE.test(value)) return false;
  if (URI_SCHEME.test(value)) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.startsWith('/\\')) return false;
  return true;
};

const getRedirectParam = (search) => {
  const raw = new URLSearchParams(search).get('redirect');
  return isSafeRedirectPath(raw) ? raw : null;
};

export {
  parseFilterQueryParams,
  queryParamsDateRangeToObject,
  queryParamStringToObject,
  queryParamsDateToObject,
  objectToQueryParamsStr,
  isSafeRedirectPath,
  getRedirectParam,
};
